import { sha256 } from "./canonical.js";
const fiveMinutes = 5 * 60_000;
const oneDay = 24 * 60 * 60_000;
function stricterMode(...modes) {
    return modes.includes("enforce") ? "enforce" : "monitor";
}
export function resolveEffectiveIssuanceGuardrails(policy, platform, profileID) {
    const profile = policy.workload_profiles[profileID]?.issuance_guardrails;
    const enterprise = policy.issuance_guardrails ?? platform.issuance_guardrails;
    return {
        mode: stricterMode(platform.issuance_guardrails.mode, enterprise.mode, profile?.mode ?? "monitor"),
        max_requests_per_requester_5m: Math.min(platform.issuance_guardrails.max_requests_per_requester_5m, enterprise.max_requests_per_requester_5m, profile?.max_requests_per_requester_5m ?? Number.MAX_SAFE_INTEGER),
        max_unique_sans_per_requester_24h: Math.min(platform.issuance_guardrails.max_unique_sans_per_requester_24h, enterprise.max_unique_sans_per_requester_24h, profile?.max_unique_sans_per_requester_24h ?? Number.MAX_SAFE_INTEGER),
        block_algorithm_downgrade: Boolean(platform.issuance_guardrails.block_algorithm_downgrade ||
            enterprise.block_algorithm_downgrade ||
            profile?.block_algorithm_downgrade),
        max_validity_increase_percent: Math.min(platform.issuance_guardrails.max_validity_increase_percent, enterprise.max_validity_increase_percent, profile?.max_validity_increase_percent ?? Number.MAX_SAFE_INTEGER),
        history_lookback_days: Math.max(platform.issuance_guardrails.history_lookback_days, enterprise.history_lookback_days, profile?.history_lookback_days ?? 0),
    };
}
function algorithmTier(algorithm) {
    switch (algorithm) {
        case "RSA-PSS-3072-SHA384": return 1;
        case "ECDSA-P256-SHA256": return 2;
        case "ECDSA-P384-SHA384": return 3;
        case "ML-DSA-65":
        case "SLH-DSA-SHA2-128s": return 4;
        case "HYBRID-ECDSA-P384-ML-DSA-65": return 5;
    }
}
function normalizeHistory(history) {
    return history.map((entry) => ({
        ...entry,
        sans: [...new Set(entry.sans.map((name) => name.toLowerCase().replace(/\.$/, "")))].sort(),
    })).sort((left, right) => left.created_at.localeCompare(right.created_at) || left.transaction_id.localeCompare(right.transaction_id));
}
export async function issuanceRiskAssessmentDigest(assessment) {
    const { evidence_sha256: _ignored, ...material } = assessment;
    return sha256(material);
}
export async function assessIssuanceRisk(input) {
    const { transaction, policy, platformPolicy, now } = input;
    const guardrails = resolveEffectiveIssuanceGuardrails(policy, platformPolicy, transaction.request.profile_id);
    const lookbackStart = now.getTime() - guardrails.history_lookback_days * oneDay;
    const history = normalizeHistory(input.history.filter((entry) => entry.requester === transaction.requester &&
        entry.profile_id === transaction.request.profile_id &&
        Date.parse(entry.created_at) >= lookbackStart &&
        Date.parse(entry.created_at) <= now.getTime()));
    const signals = [];
    const recentFiveMinutes = history.filter((entry) => Date.parse(entry.created_at) >= now.getTime() - fiveMinutes);
    const velocity = recentFiveMinutes.length + 1;
    if (velocity > guardrails.max_requests_per_requester_5m) {
        signals.push({ type: "requester_velocity", observed: velocity, limit: guardrails.max_requests_per_requester_5m, explanation: "Requester issuance velocity exceeds the signed five-minute limit" });
    }
    const recentDay = history.filter((entry) => Date.parse(entry.created_at) >= now.getTime() - oneDay);
    const uniqueSANs = new Set([...recentDay.flatMap((entry) => entry.sans), ...transaction.request.sans.map((name) => name.toLowerCase().replace(/\.$/, ""))]);
    if (uniqueSANs.size > guardrails.max_unique_sans_per_requester_24h) {
        signals.push({ type: "san_expansion", observed: uniqueSANs.size, limit: guardrails.max_unique_sans_per_requester_24h, explanation: "Requester SAN namespace expansion exceeds the signed 24-hour limit" });
    }
    const issued = history.filter((entry) => entry.status === "issued");
    const previous = issued.at(-1);
    if (previous && guardrails.block_algorithm_downgrade && algorithmTier(transaction.request.requested_algorithm) < algorithmTier(previous.requested_algorithm)) {
        signals.push({ type: "algorithm_downgrade", observed: transaction.request.requested_algorithm, limit: previous.requested_algorithm, explanation: "Requested signature posture is weaker than the latest issued identity generation" });
    }
    if (previous) {
        const allowed = Math.floor(previous.validity_seconds * (100 + guardrails.max_validity_increase_percent) / 100);
        if (transaction.request.validity_seconds > allowed) {
            signals.push({ type: "validity_escalation", observed: transaction.request.validity_seconds, limit: allowed, explanation: "Requested lifetime exceeds the signed increase budget from the latest issued generation" });
        }
    }
    const material = {
        protocol: "sectigo-edge.issuance-risk.v1",
        transaction_id: transaction.transaction_id,
        tenant_id: transaction.tenant_id,
        requester: transaction.requester,
        profile_id: transaction.request.profile_id,
        policy_id: policy.id,
        policy_version: policy.version,
        policy_sha256: input.policySHA256,
        platform_policy_id: platformPolicy.id,
        platform_policy_version: platformPolicy.version,
        platform_policy_sha256: input.platformPolicySHA256,
        effective_guardrails: guardrails,
        decision: signals.length === 0 ? "allow" : guardrails.mode === "enforce" ? "block" : "monitor",
        signals,
        history_sample_size: history.length,
        history_commitment_sha256: await sha256(history),
        evaluated_at: now.toISOString(),
    };
    return { ...material, evidence_sha256: await issuanceRiskAssessmentDigest(material) };
}
//# sourceMappingURL=issuance-risk.js.map